import React, { useCallback, useEffect, useRef, useState } from "react";

import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";

import { ReactComponent as LoadingIcon } from "@/assets/svg/loading-spinner.svg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { stripBundlePrefix } from "@/utils/bundle-name";
import { cn } from "@/utils/ui";

import { MARKER_EXCLUDE_CLASS } from "@constants/marker";

// Resolve the pdfjs worker locally from the installed package (Vite) — must
// match react-pdf's bundled pdfjs (4.8.69). The old 3.11.174 CDN URL here
// silently broke the text layer wherever this module loaded last (MDRS-134).
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

type FileData = {
  id: string;
  name: string;
  url: string;
  type: string;
};

type MergedFilePreviewProps = {
  files: FileData[];
  className?: string;
};

type PageInfo = {
  fileId: string;
  fileName: string;
  fileIndex: number;
  pageNumber: number;
  globalPageNumber: number;
};

export const MergedFilePreview: React.FC<MergedFilePreviewProps> = ({
  files,
  className = "",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1.0);
  const [pagesPerFile, setPagesPerFile] = useState<Record<string, number>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState("1");
  const [, setRenderedPages] = useState(0);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Calculate total pages and page mapping
  const totalPages = Object.values(pagesPerFile).reduce(
    (sum, count) => sum + count,
    0
  );
  const pageInfoMap: PageInfo[] = [];
  let globalPageNum = 1;

  files.forEach((file, fileIndex) => {
    const numPages = pagesPerFile[file.id] || 0;
    for (let i = 1; i <= numPages; i++) {
      pageInfoMap.push({
        fileId: file.id,
        fileName: file.name,
        fileIndex,
        pageNumber: i,
        globalPageNumber: globalPageNum++,
      });
    }
  });

  // Reset pagination when files change
  useEffect(() => {
    setCurrentPage(1);
    setPageInputValue("1");
    setRenderedPages(0);
    setPagesPerFile({});
  }, [files]);

  // Update container width
  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.clientWidth;
        if (width > 0) {
          setContainerWidth(width);
        }
      }
    };

    const timer = setTimeout(updateContainerWidth, 0);
    window.addEventListener("resize", updateContainerWidth);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateContainerWidth);
    };
  }, []);

  // Reset page refs when total pages changes
  useEffect(() => {
    pageRefs.current = Array(totalPages).fill(null);
  }, [totalPages]);

  const handleZoomIn = useCallback(
    () => setZoomLevel((z) => Math.min(z + 0.1, 3.0)),
    []
  );

  const handleZoomOut = useCallback(
    () => setZoomLevel((z) => Math.max(z - 0.1, 0.5)),
    []
  );

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      setPageInputValue(prevPage.toString());
      requestAnimationFrame(() => {
        pageRefs.current[prevPage - 1]?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      setPageInputValue(nextPage.toString());
      requestAnimationFrame(() => {
        pageRefs.current[nextPage - 1]?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    }
  };

  const handlePageInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setPageInputValue(value);

      const page = Math.min(Math.max(parseInt(value) || 1, 1), totalPages);
      if (value && !isNaN(page)) {
        setCurrentPage(page);
        pageRefs.current[page - 1]?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    },
    [totalPages]
  );

  const handleUpdateCurrentPage = useCallback(() => {
    if (!containerRef.current) return;

    const scrollElement = containerRef.current.querySelector(
      "[data-radix-scroll-area-viewport]"
    );
    if (!scrollElement) return;

    let visiblePage = 1;
    let maxVisibleArea = 0;

    pageRefs.current.forEach((ref, index) => {
      if (!ref) return;

      const rect = ref.getBoundingClientRect();
      const scrollRect = scrollElement.getBoundingClientRect();

      const visibleTop = Math.max(rect.top, scrollRect.top);
      const visibleBottom = Math.min(rect.bottom, scrollRect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibleArea = visibleHeight * rect.width;

      if (visibleArea > maxVisibleArea) {
        maxVisibleArea = visibleArea;
        visiblePage = index + 1;
      }
    });

    if (visiblePage !== currentPage) {
      setCurrentPage(visiblePage);
      setPageInputValue(visiblePage.toString());
    }
  }, [currentPage]);

  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedUpdatePage = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      requestAnimationFrame(handleUpdateCurrentPage);
    }, 50);
  }, [handleUpdateCurrentPage]);

  const handleScroll = useCallback(() => {
    debouncedUpdatePage();
  }, [debouncedUpdatePage]);

  useEffect(() => {
    const container = containerRef.current;
    const scrollElement = container?.querySelector(
      "[data-radix-scroll-area-viewport]"
    );

    if (!scrollElement) return;

    scrollElement.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollElement.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  if (files.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground">No files to preview</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid h-full grid-rows-[1fr_auto] overflow-hidden",
        MARKER_EXCLUDE_CLASS
      )}
    >
      <div
        className={cn(
          "relative h-full min-h-0 w-full min-w-0 overflow-hidden rounded-lg border bg-background shadow-sm",
          className
        )}
        ref={containerRef}
      >
        <div className="absolute right-0 top-0 z-20 flex items-center gap-2 p-1">
          <Button
            title={`Zoom out (${Math.round(zoomLevel * 100)}%)`}
            className="rounded-full p-2"
            onClick={handleZoomOut}
            disabled={zoomLevel <= 0.5}
          >
            <ZoomOut className="size-4" />
          </Button>
          <Button
            title={`Zoom in (${Math.round(zoomLevel * 100)}%)`}
            className="rounded-full p-2"
            onClick={handleZoomIn}
            disabled={zoomLevel >= 3.0}
          >
            <ZoomIn className="size-4" />
          </Button>
        </div>

        <ScrollArea className="pdf-container box-border h-full w-full flex-1 overflow-auto">
          <div className="flex min-w-full flex-col items-center space-y-4">
            {files.map((file, fileIndex) => {
              const displayName = stripBundlePrefix(file.name);
              if (file.type !== "pdf") {
                // Handle images
                if (file.type === "image") {
                  return (
                    <div key={file.id} className="relative w-full space-y-2">
                      <div className="sticky top-0 z-10 inline-flex items-center gap-2 rounded bg-primary/10 px-3 py-1.5 backdrop-blur-sm">
                        <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                          {fileIndex + 1}
                        </div>
                        <p
                          className="max-w-[500px] truncate text-sm font-medium"
                          title={displayName}
                        >
                          {displayName}
                        </p>
                      </div>
                      <div className="flex justify-center">
                        <img
                          src={file.url}
                          alt={displayName}
                          className="max-w-[90%] rounded"
                          style={{
                            transform: `scale(${zoomLevel})`,
                            transformOrigin: "center top",
                          }}
                        />
                      </div>
                    </div>
                  );
                }
                return null;
              }

              return (
                <Document
                  key={`${file.id}-zoom-${zoomLevel}`}
                  file={file.url}
                  onLoadSuccess={({ numPages }) => {
                    setPagesPerFile((prev) => ({ ...prev, [file.id]: numPages }));
                  }}
                  loading={
                    <div className="flex flex-col items-center gap-2 py-10">
                      <LoadingIcon className="h-8 w-8 animate-spin-ccw" />
                      <p className="text-sm text-foreground-muted">
                        Loading {displayName}...
                      </p>
                    </div>
                  }
                  error={
                    <div className="flex flex-col items-center gap-2 py-10">
                      <p className="font-medium text-destructive">
                        Failed to load {displayName}
                      </p>
                    </div>
                  }
                >
                  {pagesPerFile[file.id] && (
                    <>
                      <div className="sticky top-1 z-10 mb-2 inline-flex w-full items-center gap-2 rounded bg-primary-50 px-3 py-2 backdrop-blur-sm">
                        <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                          {fileIndex + 1}
                        </div>
                        <p
                          className="max-w-[500px] truncate text-sm font-medium"
                          title={displayName}
                        >
                          {displayName}
                        </p>
                      </div>
                      {/* Render all pages */}
                      {Array.from(
                        { length: pagesPerFile[file.id] },
                        (_, pageIndex) => {
                          const pageInfo = pageInfoMap.find(
                            (p) =>
                              p.fileId === file.id && p.pageNumber === pageIndex + 1
                          );
                          const globalIndex = pageInfo
                            ? pageInfo.globalPageNumber - 1
                            : 0;

                          return (
                            <div
                              key={`${file.id}-page-${pageIndex + 1}`}
                              ref={(el) => {
                                pageRefs.current[globalIndex] = el;
                              }}
                              className="page-container flex w-full justify-center"
                            >
                              <Page
                                pageNumber={pageIndex + 1}
                                width={
                                  containerWidth > 0
                                    ? containerWidth * 0.9
                                    : undefined
                                }
                                scale={zoomLevel}
                                renderAnnotationLayer={true}
                                renderTextLayer={true}
                                loading={
                                  <div className="flex justify-center pt-10">
                                    <LoadingIcon className="size-6 animate-spin-ccw" />
                                  </div>
                                }
                                onRenderSuccess={() => {
                                  setRenderedPages((prev) => prev + 1);
                                }}
                              />
                            </div>
                          );
                        }
                      )}
                    </>
                  )}
                </Document>
              );
            })}
          </div>
          <ScrollBar orientation="horizontal" className="z-10" />
          <ScrollBar orientation="vertical" className="z-10" />
        </ScrollArea>
      </div>

      <div className="flex items-center justify-between p-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground-muted">Page</span>
          <Input
            type="number"
            value={pageInputValue}
            min={1}
            max={totalPages}
            onChange={handlePageInputChange}
            onBlur={() => {
              if (!pageInputValue) {
                setPageInputValue(currentPage.toString());
              }
            }}
            className="h-8 w-16 px-2 py-1"
          />
          <span className="text-sm text-foreground-muted">of {totalPages}</span>
        </div>

        <div>
          <div className="flex gap-2">
            <Button
              variant="simple-outline"
              size="sm"
              onClick={handlePreviousPage}
              disabled={currentPage <= 1}
            >
              <ChevronLeft className="size-4" />
              Previous
            </Button>

            <Button
              variant="simple-outline"
              size="sm"
              onClick={handleNextPage}
              disabled={currentPage >= totalPages}
            >
              <ChevronRight className="size-4" />
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
